import { useMemo } from 'react';

/**
 * Why content editing is blocked in the Module Editor, if at all.
 *
 * This is the single source of truth that drives both the read-only gate and the
 * banner shown to the lecturer. Each value maps to a distinct, stakeholder-named
 * cause so the UI can explain *why* a module is locked rather than silently
 * disabling fields.
 *
 * - `none`             — the author may edit the current draft.
 * - `not_author`       — the lecturer holds a mandate (e.g. evaluator) but does
 *                        not own the module; content is permanently read-only.
 * - `version_locked`   — the version is not a draft (pending_approval /
 *                        published / retired).
 * - `rubric_generation`— an AI rubric job holds a temporary write lock.
 */
export type EditLockReason = 'none' | 'not_author' | 'version_locked' | 'rubric_generation';

/**
 * Raw facts the capability model is derived from. Kept as primitive booleans so
 * the derivation is a pure function over a small, enumerable input space and can
 * be unit-tested exhaustively (author × draft × busy × job).
 */
export interface ModuleEditorCapabilityInputs {
  /** True when the current user authored the module (owner). */
  isAuthor: boolean;
  /** True when the selected version is a draft. */
  isDraftVersion: boolean;
  /** True when the selected version is the current/active one. */
  isCurrentVersion: boolean;
  /** True when the selected version's workflow state is published. */
  isPublishedVersion: boolean;
  /** True when any version of the module is published. */
  hasPublishedVersion: boolean;
  /** Any module/task mutation in flight (transient content lock). */
  isAnyMutationPending: boolean;
  /** A save mutation in flight (gates version/approval availability). */
  saveMutationPending: boolean;
  /** An AI rubric-generation job is active. */
  isRubricGenerationJobActive: boolean;
  /** The editor form has unsaved changes. */
  formIsDirty: boolean;
  /** The lecturer-task-ai feature flag is on. */
  taskAiEnabled: boolean;
}

/**
 * Intent-revealing capabilities consumed by the editor UI. Components render
 * these booleans; they never re-derive permission or workflow logic themselves.
 */
export interface ModuleEditorCapabilities {
  isAuthor: boolean;
  editLockReason: EditLockReason;
  /** May edit module/task content right now. */
  canEditContent: boolean;
  /**
   * May edit the "autograde on submit" grading setting right now. Maps to the
   * backend `UPDATE_GRADING_SETTINGS` action: author-only, but editable on any
   * version state (exempt from the `version_locked` axis that gates content).
   */
  canEditAutograde: boolean;
  canCreateVersion: boolean;
  canDeleteVersion: boolean;
  canDeleteModule: boolean;
  canRequestApproval: boolean;
  canGenerateRubrics: boolean;
}

/**
 * Pure derivation of editor capabilities from raw facts. Exported separately so
 * it can be tested without rendering.
 */
export const deriveModuleEditorCapabilities = (
  inputs: ModuleEditorCapabilityInputs,
): ModuleEditorCapabilities => {
  const {
    isAuthor,
    isDraftVersion,
    isCurrentVersion,
    isPublishedVersion,
    hasPublishedVersion,
    isAnyMutationPending,
    saveMutationPending,
    isRubricGenerationJobActive,
    formIsDirty,
    taskAiEnabled,
  } = inputs;

  let editLockReason: EditLockReason = 'none';
  if (!isAuthor) {
    editLockReason = 'not_author';
  } else if (isRubricGenerationJobActive) {
    editLockReason = 'rubric_generation';
  } else if (!isDraftVersion) {
    editLockReason = 'version_locked';
  }

  // Version/approval actions require a settled (clean, not-saving) draft and,
  // above all, authorship — the backend enforces is_owner, so the UI must not
  // offer these actions to mandate-only viewers.
  const isSettled = !formIsDirty && !saveMutationPending;

  return {
    isAuthor,
    editLockReason,
    canEditContent: editLockReason === 'none' && !isAnyMutationPending,
    canEditAutograde: isAuthor && !isAnyMutationPending,
    canCreateVersion: isAuthor && isSettled && (isPublishedVersion || isCurrentVersion),
    canDeleteVersion: isAuthor && isSettled && !isCurrentVersion && isDraftVersion,
    canDeleteModule: isAuthor && !hasPublishedVersion,
    canRequestApproval: isAuthor && isSettled && isDraftVersion,
    canGenerateRubrics: isAuthor && isDraftVersion && taskAiEnabled,
  };
};

/**
 * useModuleEditorCapabilities
 * ----
 * Memoized capability model for the Module Editor. Centralizes the permission +
 * workflow + transient-lock logic that was previously scattered across the
 * controller and `ModuleCommandStrip`, so presentation components stay pure.
 */
export const useModuleEditorCapabilities = (
  inputs: ModuleEditorCapabilityInputs,
): ModuleEditorCapabilities => {
  const {
    isAuthor,
    isDraftVersion,
    isCurrentVersion,
    isPublishedVersion,
    hasPublishedVersion,
    isAnyMutationPending,
    saveMutationPending,
    isRubricGenerationJobActive,
    formIsDirty,
    taskAiEnabled,
  } = inputs;

  return useMemo(
    () => deriveModuleEditorCapabilities({
      isAuthor,
      isDraftVersion,
      isCurrentVersion,
      isPublishedVersion,
      hasPublishedVersion,
      isAnyMutationPending,
      saveMutationPending,
      isRubricGenerationJobActive,
      formIsDirty,
      taskAiEnabled,
    }),
    [
      isAuthor,
      isDraftVersion,
      isCurrentVersion,
      isPublishedVersion,
      hasPublishedVersion,
      isAnyMutationPending,
      saveMutationPending,
      isRubricGenerationJobActive,
      formIsDirty,
      taskAiEnabled,
    ],
  );
};
